const locale = 'fr';

const intlFormatter = new Intl.NumberFormat(locale, {});

export const decimalSeparator = ',';

/**
 * - This is Intl separator character. It's not a space.
 * - Char code 8239 in UTF-16 (https://en.wikipedia.org/wiki/Non-breaking_space).
 */
const thousandSeparator = ' ';

/**
 * - Number of relevant decimal places to which values are rounded.
 */
const precision = 2;
/**
 * - Default rounding applied during string<->number formatting.
 * - Custom rounding can be passed to formatting functions.
 * - Rounding value is a value, to multiple of which, each formatted number will be rounded.
 * - For example:
 * 1) rounding = 0.01 will allow value 0.01, and 0.02, but not 0.015.
 * 0.015 will be rounded to 0.02.
 * 2) rounding = 1000 will allow 1000 and 2000, but 1500 will be rounded to 2000.
 * - Rounding is performed by standard `Math.round`.
 */
const rounding = 1 / 10 ** precision;

/**
 * - Calculates precision (number of decimals) given rounding (smalles constituent of number).
 * - E.g. _rounding = 0.1 will give precision 1, 0.01 will give 2, 5000 will give 0 etc.
 */
const getDefaultPrecisionFromRounding = (_rounding) => {
  return _rounding === 0 ? Infinity : Math.max(0, Number.parseInt(String(Math.log10(1 / _rounding)), 10));
};

/**
 * Functions formatting number to string-number in one of recognized formats.
 */
const formatNumberToString = {
  /* 1231.123 -> 1 234,12 */
  /**
   * - Format A.
   * - Follows Intl formatting (UTF-16 8239 as thousands separator and comma as decimals separator).
   * - Decimals truncated to 2 spaces with no rounding.
   */
  A: (n: number, decimalPlaces: number) => {
    const intlFormatted = intlFormatter.format(n);
    const split = intlFormatted.split(decimalSeparator);

    // Trim decimal to {precision} digits
    const decimal = split[1]?.slice(0, decimalPlaces ?? precision) ?? /* no decimal part present */ '';

    return `${split[0]}${decimal && decimalSeparator}${decimal}`;
  },
};

// eslint-disable-next-line no-irregular-whitespace
const validStringNubmerCharactersRegexp = /^[0-9  ,.]*$/;

/**
 * - Check whether string contains only characters allowing to resonably try interpret it as a number.
 */
const isStringNumberValid = (maybeStringNumber: string) => {
  return validStringNubmerCharactersRegexp.test(maybeStringNumber);
};

const trimDecimals = (number: number, decimalPlaces: number) => {
  if (decimalPlaces === Infinity) return number;

  const stringNumber = String(number);
  const split = stringNumber.split('.');
  // Trim decimal to {precision} digits
  const decimal = split[1]?.slice(0, decimalPlaces ?? precision) ?? /* no decimal part present */ '';

  return Number(`${split[0]}${decimal && '.'}${decimal}`);
};

/**
 * - Rounds number and trims decimals.
 * @param _rounding - The value the multiple of which should the final number be (e.g. 0.01, or 1000)
 */
export const roundNumber = (number: number | undefined, _rounding = rounding): number => {
  if (!number) return 0;

  const decimalPlaces = getDefaultPrecisionFromRounding(_rounding);
  const roundedNumber = _rounding > 0 ? Number(Math.round(number / _rounding) * _rounding) : number;
  return trimDecimals(roundedNumber, decimalPlaces);
};

/**
 * - Convert string value (representing number according to one of supported by the application formats)
 * to JS numeric type value.
 * - In case of actual number - only trim it to supported precision.
 * - e.g.: 11,54 (string) -> 11.54 (number)
 */
export const stringNumberToNumber = (stringNumber: string | number | undefined, _rounding = rounding): number => {
  if (!stringNumber) return 0;
  if (!isStringNumberValid(String(stringNumber))) return 0;

  let maybeNumber;

  if (typeof stringNumber === 'number') {
    maybeNumber = stringNumber;
  } else {
    maybeNumber = Number(
      stringNumber
        .replace(decimalSeparator, '.') /* replace decimal separator with one recognized by JS */
        .replaceAll(thousandSeparator, '') /* remove thousand separators used by Intl */
        .replaceAll(' ', '') /* remove any whitespace */
    );
  }

  if (Number.isNaN(maybeNumber)) return 0;

  const number = maybeNumber;

  return roundNumber(number, _rounding);
};

/**
 * - Convert JS numeric type value to string matching one of supported by the application formats.
 */
const numberToStringNumber = (number: number | undefined, decimalPlaces = 2): string => {
  if (!number) return '0';

  return formatNumberToString.A(number, decimalPlaces);
};

/**
 * - Takes value expected to be valid string representation of number or actual number.
 * - Return this value aligned with desired format.
 */
export const formatStringNumber = (
  stringNumber: string | number | undefined,
  _rounding = rounding,
  decimalPlaces = 2
): string => {
  return numberToStringNumber(stringNumberToNumber(stringNumber, _rounding), decimalPlaces);
};

/**
 * - Appends missing decimal part digits to the number up to desired number of decimal places.
 * - E.g. for stringNumber=11, decimalPlaces=2 the result is 11,00.
 * - _rounding >= 1 means that this quantity never occurs with decimal part so we won't be adding it either.
 */
export const appendMissingDecimalPart = (
  stringNumber: string | number,
  decimalPlaces: number,
  _rounding = rounding
) => {
  stringNumber = String(stringNumber).replace('.', decimalSeparator);

  if (decimalPlaces === 0 || _rounding >= 1) {
    return stringNumber;
  }

  const integralPart = stringNumber.split(decimalSeparator)[0];
  const decimalPart = stringNumber.split(decimalSeparator)[1] ?? '';

  const newDecimalPart = decimalPart.padEnd(decimalPlaces, '0');

  return `${integralPart}${decimalSeparator}${newDecimalPart}`;
};

export const sumValues = (...values: number[]) => {
  return values.reduce((sum, value) => sum + value, 0);
};
